import type { Variants } from 'motion/react';

/**
 * Animation duration standards following the design system.
 *
 * Timing standards:
 * - fast: 150ms - For micro-interactions, hover states, and quick feedback
 * - normal: 300ms - Default for most UI transitions and component animations
 * - slow: 500ms - For complex animations or when emphasis is needed
 *
 * @example
 * ```tsx
 * transition={{ duration: ANIMATION_DURATIONS.fast }}
 * ```
 */
export const ANIMATION_DURATIONS = {
  fast: 0.15,
  normal: 0.3,
  slow: 0.5,
} as const;

/**
 * Easing functions for smooth animations.
 *
 * Easing preferences:
 * - easeOut: [0.16, 1, 0.3, 1] - Default for most animations (smooth deceleration)
 * - easeInOut: [0.4, 0, 0.2, 1] - For bidirectional animations
 * - easeIn: [0.4, 0, 1, 1] - For exit animations
 * - spring: { type: 'spring', stiffness: 300, damping: 30 } - For natural, bouncy animations
 *
 * @example
 * ```tsx
 * transition={{ duration: ANIMATION_DURATIONS.normal, ease: EASING.easeOut }}
 * ```
 */
export const EASING = {
  easeOut: [0.16, 1, 0.3, 1],
  easeInOut: [0.4, 0, 0.2, 1],
  easeIn: [0.4, 0, 1, 1],
  spring: { type: 'spring', stiffness: 300, damping: 30 },
} as const;

/**
 * Fade in/out animation variant.
 *
 * Use for: Simple opacity transitions, content appearing/disappearing
 * Duration: normal (300ms) for enter, fast (150ms) for exit
 *
 * @example
 * ```tsx
 * <motion.div variants={fadeInOut} initial="hidden" animate="visible" exit="exit">
 *   Content
 * </motion.div>
 * ```
 */
export const fadeInOut: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: ANIMATION_DURATIONS.normal, ease: EASING.easeOut },
  },
  exit: {
    opacity: 0,
    transition: { duration: ANIMATION_DURATIONS.fast, ease: EASING.easeIn },
  },
};

/**
 * Slide in/out animation variant with scale effect.
 *
 * Use for: Cards, panels, dropdowns, and elements entering from above/below
 * Duration: normal (300ms) with spring physics
 *
 * @example
 * ```tsx
 * <motion.div variants={slideInOut} initial="hidden" animate="visible" exit="exit">
 *   Sliding content
 * </motion.div>
 * ```
 */
export const slideInOut: Variants = {
  hidden: {
    opacity: 0,
    y: -10,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: ANIMATION_DURATIONS.normal,
      ease: EASING.easeOut,
      type: 'spring',
      stiffness: 300,
      damping: 30,
    },
  },
  exit: {
    opacity: 0,
    y: -10,
    scale: 0.95,
    transition: { duration: ANIMATION_DURATIONS.fast, ease: EASING.easeIn },
  },
};

/**
 * Scale in/out animation variant.
 *
 * Use for: Popovers, tooltips, and elements that need to grow/shrink
 * Duration: normal (300ms) with spring physics for natural feel
 *
 * @example
 * ```tsx
 * <motion.div variants={scaleInOut} initial="hidden" animate="visible" exit="exit">
 *   Scaling content
 * </motion.div>
 * ```
 */
export const scaleInOut: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.8,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: ANIMATION_DURATIONS.normal,
      ease: EASING.easeOut,
      type: 'spring',
      stiffness: 400,
      damping: 25,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.8,
    transition: { duration: ANIMATION_DURATIONS.fast, ease: EASING.easeIn },
  },
};

/**
 * Modal/Dialog overlay animation variant.
 *
 * Use for: Dialog and modal backdrop overlays
 * Duration: normal (300ms) for smooth fade-in/out
 *
 * @example
 * ```tsx
 * <motion.div variants={modalOverlay} initial="hidden" animate="visible" exit="exit">
 *   Overlay backdrop
 * </motion.div>
 * ```
 */
export const modalOverlay: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: ANIMATION_DURATIONS.normal, ease: EASING.easeOut },
  },
  exit: {
    opacity: 0,
    transition: { duration: ANIMATION_DURATIONS.fast, ease: EASING.easeIn },
  },
};

/**
 * Modal/Dialog content animation variant.
 *
 * Use for: Dialog and modal content containers
 * Duration: normal (300ms) with spring physics for smooth entrance
 * Combines scale and vertical slide for polished appearance
 *
 * @example
 * ```tsx
 * <motion.div variants={modalContent} initial="hidden" animate="visible" exit="exit">
 *   Modal content
 * </motion.div>
 * ```
 */
export const modalContent: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.9,
    y: 20,
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: ANIMATION_DURATIONS.normal,
      ease: EASING.easeOut,
      type: 'spring',
      stiffness: 300,
      damping: 30,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.9,
    y: 20,
    transition: { duration: ANIMATION_DURATIONS.fast, ease: EASING.easeIn },
  },
};

/**
 * Dialog content animation variant (centered, no Y translation).
 *
 * Use for: Centered dialogs that use CSS transforms for positioning
 * Duration: normal (300ms) with spring physics for smooth entrance
 * Only uses opacity and scale to avoid conflicts with CSS centering transforms
 *
 * @example
 * ```tsx
 * <motion.div variants={dialogContent} initial="hidden" animate="visible" exit="exit">
 *   Dialog content
 * </motion.div>
 * ```
 */
export const dialogContent: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: ANIMATION_DURATIONS.normal,
      ease: EASING.easeOut,
      type: 'spring',
      stiffness: 300,
      damping: 30,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: { duration: ANIMATION_DURATIONS.fast, ease: EASING.easeIn },
  },
};

/**
 * Toast notification slide-in animation variant.
 *
 * Use for: Toast notifications sliding in from the side
 * Duration: normal (300ms) with spring physics
 * Slides from right (100%) to center (0)
 *
 * @example
 * ```tsx
 * <motion.div variants={toastSlideIn} initial="hidden" animate="visible" exit="exit">
 *   Toast message
 * </motion.div>
 * ```
 */
export const toastSlideIn: Variants = {
  hidden: {
    opacity: 0,
    x: '100%',
    scale: 0.9,
  },
  visible: {
    opacity: 1,
    x: 0,
    scale: 1,
    transition: {
      duration: ANIMATION_DURATIONS.normal,
      ease: EASING.easeOut,
      type: 'spring',
      stiffness: 300,
      damping: 30,
    },
  },
  exit: {
    opacity: 0,
    x: '100%',
    scale: 0.9,
    transition: { duration: ANIMATION_DURATIONS.fast, ease: EASING.easeIn },
  },
};

/**
 * Dropdown menu animation variant.
 *
 * Use for: Dropdown menus, select dropdowns, and context menus
 * Duration: fast (150ms) for quick, responsive feel
 * Combines scale and vertical slide
 *
 * @example
 * ```tsx
 * <motion.div variants={dropdownMenu} initial="hidden" animate="visible" exit="exit">
 *   Dropdown content
 * </motion.div>
 * ```
 */
export const dropdownMenu: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
    y: -8,
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: ANIMATION_DURATIONS.fast,
      ease: EASING.easeOut,
      type: 'spring',
      stiffness: 400,
      damping: 25,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: -8,
    transition: { duration: ANIMATION_DURATIONS.fast, ease: EASING.easeIn },
  },
};

/**
 * Tooltip animation variant.
 *
 * Use for: Tooltips and small informational popovers
 * Duration: fast (150ms) for quick appearance
 * Subtle scale and vertical movement
 *
 * @example
 * ```tsx
 * <motion.div variants={tooltip} initial="hidden" animate="visible" exit="exit">
 *   Tooltip text
 * </motion.div>
 * ```
 */
export const tooltip: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.8,
    y: 4,
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: ANIMATION_DURATIONS.fast,
      ease: EASING.easeOut,
      type: 'spring',
      stiffness: 500,
      damping: 30,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.8,
    y: 4,
    transition: { duration: ANIMATION_DURATIONS.fast, ease: EASING.easeIn },
  },
};

/**
 * Accordion content animation variant.
 *
 * Use for: Accordion expand/collapse animations
 * Duration: normal (300ms) for height transitions, fast (150ms) for opacity
 * Handles height: auto transitions smoothly
 *
 * @example
 * ```tsx
 * <motion.div variants={accordionContent} initial="hidden" animate="visible" exit="exit">
 *   Accordion content
 * </motion.div>
 * ```
 */
export const accordionContent: Variants = {
  hidden: {
    height: 0,
    opacity: 0,
  },
  visible: {
    height: 'auto',
    opacity: 1,
    transition: {
      duration: ANIMATION_DURATIONS.normal,
      ease: EASING.easeInOut,
      height: { duration: ANIMATION_DURATIONS.normal, ease: EASING.easeInOut },
      opacity: { duration: ANIMATION_DURATIONS.fast, delay: 0.1 },
    },
  },
  exit: {
    height: 0,
    opacity: 0,
    transition: {
      duration: ANIMATION_DURATIONS.normal,
      ease: EASING.easeInOut,
      height: { duration: ANIMATION_DURATIONS.normal, ease: EASING.easeInOut },
      opacity: { duration: ANIMATION_DURATIONS.fast },
    },
  },
};

/**
 * Tab content animation variant.
 *
 * Use for: Tab panel transitions when switching between tabs
 * Duration: normal (300ms) for enter, fast (150ms) for exit
 * Horizontal slide effect (x-axis movement)
 *
 * @example
 * ```tsx
 * <motion.div variants={tabContent} initial="hidden" animate="visible" exit="exit">
 *   Tab panel content
 * </motion.div>
 * ```
 */
export const tabContent: Variants = {
  hidden: {
    opacity: 0,
    x: 20,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: ANIMATION_DURATIONS.normal,
      ease: EASING.easeOut,
    },
  },
  exit: {
    opacity: 0,
    x: -20,
    transition: { duration: ANIMATION_DURATIONS.fast, ease: EASING.easeIn },
  },
};

/**
 * Popover animation variant.
 *
 * Use for: Popovers and floating content panels
 * Duration: fast (150ms) for quick, responsive feel
 * Combines scale and subtle vertical movement
 *
 * @example
 * ```tsx
 * <motion.div variants={popover} initial="hidden" animate="visible" exit="exit">
 *   Popover content
 * </motion.div>
 * ```
 */
export const popover: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
    y: -4,
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: ANIMATION_DURATIONS.fast,
      ease: EASING.easeOut,
      type: 'spring',
      stiffness: 400,
      damping: 25,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: -4,
    transition: { duration: ANIMATION_DURATIONS.fast, ease: EASING.easeIn },
  },
};

/**
 * Hover scale animation for interactive elements.
 *
 * Use for: Buttons, cards, and clickable elements on hover
 * Duration: fast (150ms) for responsive feedback
 *
 * @example
 * ```tsx
 * <motion.div whileHover={hoverScale}>
 *   Hoverable element
 * </motion.div>
 * ```
 */
export const hoverScale = {
  scale: 1.02,
  transition: { duration: ANIMATION_DURATIONS.fast, ease: EASING.easeOut },
};

/**
 * Hover lift animation for interactive elements.
 *
 * Use for: Cards and elevated elements on hover
 * Duration: fast (150ms) for responsive feedback
 *
 * @example
 * ```tsx
 * <motion.div whileHover={hoverLift}>
 *   Hoverable card
 * </motion.div>
 * ```
 */
export const hoverLift = {
  y: -2,
  transition: { duration: ANIMATION_DURATIONS.fast, ease: EASING.easeOut },
};

/**
 * Stagger container animation variant for lists.
 *
 * Use for: Container elements that need to stagger child animations
 * Stagger delay: 100ms between children
 *
 * @example
 * ```tsx
 * <motion.div variants={staggerContainer} initial="hidden" animate="visible">
 *   {items.map(item => (
 *     <motion.div key={item.id} variants={staggerItem}>
 *       {item.content}
 *     </motion.div>
 *   ))}
 * </motion.div>
 * ```
 */
export const staggerContainer: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

/**
 * Stagger item animation variant for list items.
 *
 * Use for: Individual items within a staggered list
 * Duration: normal (300ms) with vertical slide effect
 *
 * @example
 * ```tsx
 * <motion.div variants={staggerItem}>
 *   List item content
 * </motion.div>
 * ```
 */
export const staggerItem: Variants = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: ANIMATION_DURATIONS.normal,
      ease: EASING.easeOut,
    },
  },
};

/**
 * Utility function to create custom animation variants.
 *
 * Use when: You need a custom animation that doesn't fit existing presets
 * Defaults: duration = normal (300ms), ease = easeOut
 *
 * @param config - Configuration object for custom variants
 * @param config.hidden - Initial/hidden state properties
 * @param config.visible - Visible state properties
 * @param config.exit - Exit state properties
 * @param config.duration - Animation duration (defaults to normal)
 * @param config.ease - Easing function (defaults to easeOut)
 *
 * @returns Custom Variants object compatible with motion/react
 *
 * @example
 * ```tsx
 * const customVariants = createVariants({
 *   hidden: { opacity: 0, x: -50 },
 *   visible: { opacity: 1, x: 0 },
 *   exit: { opacity: 0, x: 50 },
 *   duration: ANIMATION_DURATIONS.slow
 * });
 * ```
 */
export const createVariants = (config: {
  hidden?: Record<string, unknown>;
  visible?: Record<string, unknown>;
  exit?: Record<string, unknown>;
  duration?: number;
  ease?: number[] | string;
}) => {
  const {
    hidden,
    visible,
    exit,
    duration = ANIMATION_DURATIONS.normal,
    ease = EASING.easeOut,
  } = config;

  return {
    hidden: hidden || { opacity: 0 },
    visible: {
      ...visible,
      transition: { duration, ease },
    },
    exit: {
      ...exit,
      transition: { duration: ANIMATION_DURATIONS.fast, ease: EASING.easeIn },
    },
  };
};

/**
 * Animation presets collection for easy access.
 *
 * Contains all predefined animation variants for common use cases.
 * Use this object to programmatically select animations.
 *
 * @example
 * ```tsx
 * const animationType: AnimationPreset = 'fadeInOut';
 * <motion.div variants={ANIMATION_PRESETS[animationType]}>
 *   Content
 * </motion.div>
 * ```
 */
export const ANIMATION_PRESETS = {
  fadeInOut,
  slideInOut,
  scaleInOut,
  modalOverlay,
  modalContent,
  toastSlideIn,
  dropdownMenu,
  tooltip,
  accordionContent,
  tabContent,
  popover,
  staggerContainer,
  staggerItem,
} as const;

export type AnimationPreset = keyof typeof ANIMATION_PRESETS;
